/** File with folder-relative path (same shape as webkitdirectory uploads). */
export type ImportFolderFile = File & { webkitRelativePath: string };

/** Minimal File System Access API types (not in all TS DOM libs). */
interface ImportFolderFileHandle {
  kind: 'file';
  name: string;
  getFile(): Promise<File>;
}

export interface ImportDirectoryHandle {
  kind: 'directory';
  name: string;
  values(): AsyncIterable<ImportFolderFileHandle | ImportDirectoryHandle>;
}

interface ImportDirectoryPickerWindow extends Window {
  showDirectoryPicker?(options?: { mode?: 'read' | 'readwrite' }): Promise<ImportDirectoryHandle>;
}

function importPickerWindow(): ImportDirectoryPickerWindow | null {
  if (typeof window === 'undefined') return null;
  return window as unknown as ImportDirectoryPickerWindow;
}

function isDevBuild(): boolean {
  return process.env.NODE_ENV === 'development';
}

/** LAN dev over plain HTTP (e.g. http://192.168.x.x:3000) — uses webkitdirectory; Chrome may show its own prompt first. */
export function isImportFolderLanDev(): boolean {
  const w = importPickerWindow();
  return Boolean(w && !w.isSecureContext && isDevBuild());
}

/**
 * Folder import on HTTPS/localhost always; on LAN HTTP only in development builds.
 */
export function isImportFolderAvailable(): boolean {
  const w = importPickerWindow();
  if (!w) return false;
  return w.isSecureContext || isDevBuild();
}

function hasDirectoryPicker(): boolean {
  const w = importPickerWindow();
  return Boolean(w && typeof w.showDirectoryPicker === 'function');
}

function attachRelativePath(file: File, relativePath: string): ImportFolderFile {
  Object.defineProperty(file, 'webkitRelativePath', {
    value: relativePath,
    configurable: true,
  });
  return file as ImportFolderFile;
}

/** Read all files from a directory handle (after user confirms upload). */
export async function readImportFolderFiles(
  handle: ImportDirectoryHandle,
  rootName: string,
  onProgress?: (info: { fileCount: number; latestPath: string }) => void,
): Promise<ImportFolderFile[]> {
  return readDirectoryRecursive(handle, rootName, onProgress);
}

async function readDirectoryRecursive(
  handle: ImportDirectoryHandle,
  prefix: string,
  onProgress?: (info: { fileCount: number; latestPath: string }) => void,
): Promise<ImportFolderFile[]> {
  const out: ImportFolderFile[] = [];
  for await (const entry of handle.values()) {
    const relativePath = prefix ? `${prefix}/${entry.name}` : entry.name;
    if (entry.kind === 'file') {
      const file = await entry.getFile();
      out.push(attachRelativePath(file, relativePath));
      onProgress?.({ fileCount: out.length, latestPath: relativePath });
    } else if (entry.kind === 'directory') {
      const fileCountBeforeSub = out.length;
      out.push(
        ...(await readDirectoryRecursive(entry, relativePath, (info) => {
          onProgress?.({
            fileCount: fileCountBeforeSub + info.fileCount,
            latestPath: info.latestPath,
          });
        })),
      );
    }
  }
  return out;
}

export type ImportFolderPickResult =
  | { mode: 'directory'; handle: ImportDirectoryHandle; folderName: string }
  | { mode: 'legacy' };

/**
 * Open a folder picker. Returns null if cancelled or unavailable.
 * Prefers showDirectoryPicker on secure origins; falls back to webkitdirectory elsewhere in dev.
 */
export async function pickImportFolder(): Promise<ImportFolderPickResult | null> {
  const w = importPickerWindow();
  if (!w || !isImportFolderAvailable()) return null;

  if (w.isSecureContext && hasDirectoryPicker()) {
    try {
      const handle = await w.showDirectoryPicker!({ mode: 'read' });
      return { mode: 'directory', handle, folderName: handle.name };
    } catch (err: unknown) {
      const name = err instanceof DOMException ? err.name : '';
      if (name === 'AbortError') return null;
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        return { mode: 'legacy' };
      }
      throw err;
    }
  }

  return { mode: 'legacy' };
}

export function folderNameFromWebkitFiles(files: File[]): string {
  const rel = (files[0] as File & { webkitRelativePath?: string }).webkitRelativePath;
  if (rel) {
    const segment = rel.split('/')[0];
    if (segment) return segment;
  }
  return 'the selected folder';
}

function getFsEntryFromItem(item: DataTransferItem): FileSystemEntry | null {
  return item.webkitGetAsEntry?.() ?? null;
}

async function readFsFileEntry(entry: FileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => entry.file(resolve, reject));
}

async function readFsDirectoryEntries(entry: FileSystemDirectoryEntry): Promise<FileSystemEntry[]> {
  const reader = entry.createReader();
  const all: FileSystemEntry[] = [];
  for (;;) {
    const batch = await new Promise<FileSystemEntry[]>((resolve, reject) => reader.readEntries(resolve, reject));
    if (!batch.length) break;
    all.push(...batch);
  }
  return all;
}

async function readFsDirectoryTree(
  entry: FileSystemDirectoryEntry,
  prefix: string,
  out: ImportFolderFile[],
): Promise<void> {
  const children = await readFsDirectoryEntries(entry);
  for (const child of children) {
    const childPath = prefix ? `${prefix}/${child.name}` : child.name;
    if (child.isFile) {
      const file = await readFsFileEntry(child as FileSystemFileEntry);
      out.push(attachRelativePath(file, childPath));
    } else if (child.isDirectory) {
      await readFsDirectoryTree(child as FileSystemDirectoryEntry, childPath, out);
    }
  }
}

/** Read files from a drag-and-drop event, preserving folder structure when a directory is dropped. */
export async function readDataTransferImportFiles(
  dataTransfer: DataTransfer,
): Promise<{ files: ImportFolderFile[]; folderName: string }> {
  const items = Array.from(dataTransfer.items).filter((item) => item.kind === 'file');
  const entries = items.map(getFsEntryFromItem).filter((entry): entry is FileSystemEntry => entry != null);

  if (entries.length > 0) {
    const files: ImportFolderFile[] = [];
    if (entries.length === 1 && entries[0].isDirectory) {
      const root = entries[0] as FileSystemDirectoryEntry;
      await readFsDirectoryTree(root, root.name, files);
      return { files, folderName: root.name };
    }

    for (const entry of entries) {
      if (entry.isFile) {
        const file = await readFsFileEntry(entry as FileSystemFileEntry);
        files.push(attachRelativePath(file, entry.name));
      } else if (entry.isDirectory) {
        await readFsDirectoryTree(entry as FileSystemDirectoryEntry, entry.name, files);
      }
    }

    const folderName =
      entries.length === 1 ? entries[0].name : folderNameFromWebkitFiles(files) || 'Dropped items';
    return { files, folderName };
  }

  const flatFiles = Array.from(dataTransfer.files);
  return {
    files: flatFiles.map((file) => attachRelativePath(file, file.name)),
    folderName: folderNameFromWebkitFiles(flatFiles) || 'Dropped files',
  };
}
